
import { ref, computed } from 'vue'

// 风险覆盖矩阵类型定义
export type PathogenType = 'bodyGill' | 'body' | 'digestive' | 'bacterial'
export interface PathogenCoverage {
  'bodyGill': Record<string, string[]>
  'body': Record<string, string[]>
  'digestive': Record<string, string[]>
  'bacterial': Record<string, string[]>
}

// 目测检查项
export type VisualCheckItem = 
  | 'eyeLeft' | 'eyeRight' | 'mouthIntact' | 'gillLeftNormal' | 'gillRightNormal'
  | 'pectoralL' | 'pectoralR' | 'dorsalFin' | 'dorsalExtends' | 'pelvicL'
  | 'pelvicR' | 'pelvicExtends' | 'bodyLeft' | 'bodyRight' | 'caudalFin'

// 步骤类型
export type StepType = 
  | 'observation'   // 观察期
  | 'visualCheck'   // 目测检查
  | 'bath'          // 药浴
  | 'maintenance'   // 维持期（加阻断剂/喂食）
  | 'bucketSwap'    // AB桶换桶
  | 'praziTreatment'// 吡喹酮治疗
  | 'completion'    // 完成

// 药物定义
export interface DrugDose {
  name: string                     // 药物名
  targetPPM?: number               // 目标 ppm（可选）
  dose_ml_per_L?: number           // ml/L（如福尔马林 0.2ml/L）
  dose_ml_per_10L?: number         // ml/10L（如 HW Odinex 1ml/20L → 0.5ml/10L）
  dose_ml_per_20L?: number         // ml/20L（如 HW Odinex 1ml/20L）
  dose_ml_per_75L?: number         // ml/75L（如 PraziPro 5ml/75L）
  dose_mg_per_L?: number           // mg/L（如磷酸氯喹 25mg/L）
  tabletPerX_L?: [number, number]  // [片数, 对应水量L]，如 [1, 40] 表示1片/40L
  note?: string                    // 特别说明（如"仅首日高剂量"）
}

// 检疫方案类型定义
export interface QuarantinePlan {
  id: string
  name: string
  description: string
  duration: number
  coverageWarning?: string
  pathogenCoverage: PathogenCoverage
  steps: QuarantineStep[]
  fishTypes: string[]
  warnings: string[]
}

export interface QuarantineStep {
  day: string                      // 如 'T+0~T+2', 'T+3', 'T+10'
  name: string                     // 步骤名称
  type: StepType
  drugs?: DrugDose[]               // 药物列表
  actions: string[]                // 必做操作（如"换水100%"、"拆蛋分收集杯"）
  safetyNotes: string[]            // ⚠ 安全警告（手册原文精简）
  equipmentAdjustments?: string[]  // 设备调整（UV/蛋分/滤材等）
  required?: boolean               // 是否强制（如观察期）
  isCompleted?: boolean
  completedDate?: string
  fishStatus?: FishStatus
  visualCheckResults?: Partial<Record<VisualCheckItem, boolean>>
}

export interface FishStatus {
  appetite: 'good' | 'normal' | 'poor' | 'none'
  breathing: 'normal' | 'fast' | 'labored'
  symptoms: string[]
  notes?: string
}

// 药品规格数据库
export const MEDICATION_SPECS = {
  'formalin': {
    name: 'quarantine.medications.formalin',
    concentration: 370, // mg/ml (37% = 370mg/ml)
    unit: 'ml',
    notes: 'Ready‑to‑use formalin; do not convert to pure formaldehyde'
  },
  'malachite-green': {
    name: 'quarantine.medications.malachiteGreen',
    concentration: 1, // mg/ml (assumed 1mg/ml stock solution)
    unit: 'ml',
    notes: 'Usually used as stock solution'
  },
  'chloroquine-phosphate': {
    name: 'quarantine.medications.chloroquinePhosphate',
    concentration: 1, // mg/mg (pure)
    unit: 'mg',
    notes: 'Use pure powder'
  },
  'metronidazole': {
    name: 'quarantine.medications.metronidazole',
    concentration: 1, // mg/mg (pure)
    unit: 'mg',
    notes: 'Use pure powder'
  },
  'praziquantel': {
    name: 'quarantine.medications.praziquantel',
    concentration: 20, // mg/ml (assumed 20mg/ml stock solution)
    unit: 'ml',
    notes: 'Usually used as stock solution'
  },
  'hw-odinex': {
    name: 'quarantine.medications.hwOdinex',
    concentration: 1, // according to product instructions
    unit: 'ml',
    notes: 'Use according to product instructions'
  },
  'copper': {
    name: 'quarantine.medications.copper',
    concentration: 1, // according to product instructions
    unit: 'ml',
    notes: 'Use according to product instructions'
  }
}

// 风险覆盖矩阵
export const PATHOGEN_COVERAGE: PathogenCoverage = {
  'bodyGill': {
    'quarantine.diseases.whiteSpot': ['quarantine.medications.formalin', 'quarantine.medications.malachiteGreen', 'quarantine.medications.copper', 'quarantine.medications.chloroquinePhosphate'],
    'quarantine.diseases.discusWorm': ['quarantine.medications.formalin', 'quarantine.medications.malachiteGreen', 'quarantine.medications.chloroquinePhosphate'],
    'quarantine.diseases.ovale': ['quarantine.medications.copper', 'quarantine.medications.chloroquinePhosphate'],
    'quarantine.diseases.bacterialGillRot': ['quarantine.medications.formalin']
  },
  'body': {
    'quarantine.diseases.whiteFilm': ['quarantine.medications.formalin', 'quarantine.medications.malachiteGreen'],
    'quarantine.diseases.redSpot': ['quarantine.medications.formalin'],
    'quarantine.diseases.velvet': ['quarantine.medications.copper']
  },
  'digestive': {
    'quarantine.diseases.intestinalFlagellate': ['quarantine.medications.metronidazole'],
    'quarantine.diseases.intestinalFluke': ['quarantine.medications.praziquantel'],
    'quarantine.diseases.intestinalTapeworm': ['quarantine.medications.praziquantel']
  },
  'bacterial': {
    'quarantine.diseases.bacterialInfection': ['quarantine.medications.formalin'],
    'quarantine.diseases.fungalInfection': ['quarantine.medications.malachiteGreen']
  }
}

// 5种标准检疫方案
export const QUARANTINE_PLANS: QuarantinePlan[] = [
  {
    id: '3day',
    name: 'quarantine.plans.3day.name',
    description: 'quarantine.plans.3day.description',
    duration: 3,
    coverageWarning: 'quarantine.plans.3day.coverageWarning',
    pathogenCoverage: PATHOGEN_COVERAGE,
    fishTypes: ['quarantine.options.fishType.all'],
    warnings: ['quarantine.plans.3day.warnings.recommendedForHealthyFish', 'quarantine.plans.3day.warnings.noDigestiveCoverage'],
    steps: [
      {
        day: 'T+0~T+3',
        name: 'quarantine.plans.3day.steps.observationPeriod',
        type: 'observation',
        required: true,
        actions: [
          'quarantine.actions.noFeeding',
          'quarantine.actions.noMedication',
          'quarantine.actions.noVcGlucoseYogurt',
          'quarantine.actions.reduceDisturbance'
        ],
        safetyNotes: [
          'quarantine.safetyNotes.transportStress'
        ]
      },
      {
        day: 'T+3',
        name: 'quarantine.plans.3day.steps.formalinMalachiteGreenBath',
        type: 'bath',
        drugs: [
          {
            name: 'quarantine.medications.formalin',
            dose_ml_per_L: 0.2,
            note: 'Ready‑to‑use 37% formaldehyde solution; do not convert to pure formaldehyde'
          },
          {
            name: 'quarantine.medications.malachiteGreen',
            dose_ml_per_L: 0.1,
            note: '0.1g/100ml stock solution'
          }
        ],
        actions: [
          'Medication bath for 45 minutes',
          'Split dosing: add half first, wait 5 minutes, then add the rest',
          'Oxygenate throughout',
          'Ventilate operation',
          'Transfer to main tank after bath'
        ],
        safetyNotes: [
          '⚠ Formalin consumes dissolved oxygen, lack of oxygenation can kill fish',
          '⚠ Formaldehyde gas is toxic, ensure ventilation + wear mask',
          '⚠ If fish struggles violently/floats, immediately change water to rescue'
        ],
        equipmentAdjustments: [
          'Use transparent container for side viewing',
          'Avoid top-view tank (cannot inspect body side)'
        ]
      }
    ]
  },
  {
    id: '17day-httm',
    name: 'quarantine.plans.17dayHttm.name',
    description: 'quarantine.plans.17dayHttm.description',
    duration: 17,
    pathogenCoverage: PATHOGEN_COVERAGE,
    fishTypes: ['quarantine.options.fishType.all'],
    warnings: ['quarantine.plans.17dayHttm.warnings.disableNitrifyingFilter', 'quarantine.plans.17dayHttm.warnings.needWaterChanges'],
    steps: [
      {
        day: 'T+0~T+2',
        name: 'quarantine.plans.17dayHttm.steps.observationPeriod',
        type: 'observation',
        required: true,
        actions: ['quarantine.actions.sameAs3DayPlan'],
        safetyNotes: []
      },
      {
        day: 'T+3',
        name: 'quarantine.plans.17dayHttm.steps.firstBathAbBucketStart',
        type: 'bath',
        drugs: [
          {
            name: 'quarantine.medications.formalin',
            dose_ml_per_L: 0.2
          },
          {
            name: 'quarantine.medications.malachiteGreen',
            dose_ml_per_L: 0.1
          }
        ],
        actions: [
          'Medication bath for 45 minutes (same procedure as 3‑day plan)',
          'Transfer to new bucket (A→B) after bath',
          'Disinfect old bucket with chlorine dioxide tablets and air dry',
          'Add blocking agent to new bucket (choose 1 of 4, do not combine)'
        ],
        safetyNotes: [
          '⚠ Formalin bath safety regulations as above'
        ]
      },
      {
        day: 'T+3~T+16',
        name: 'quarantine.plans.17dayHttm.steps.abBucketCycle',
        type: 'bucketSwap',
        actions: [
          'quarantine.actions.bucketSwapEvery2Days',
          'quarantine.actions.disinfectAndDry'
        ],
        drugs: [
          {
            name: 'quarantine.medications.hwOdinex',
            dose_ml_per_20L: 1,
            note: 'Most recommended; requires 100% water change every 3 days to prevent copper accumulation'
          },
          {
            name: 'quarantine.medications.chloroquinePhosphate',
            dose_mg_per_L: 25,
            note: 'Check fish tolerance; disable nitrifying filter media'
          },
          {
            name: 'quarantine.medications.copper',
            tabletPerX_L: [0.5, 40],
            note: 'First day 0.5ml/40L, then repeat every 24h ×2 times; maintain for 14 days'
          },
          {
            name: 'quarantine.medications.metronidazole',
            dose_mg_per_L: 10,
            note: 'Better than nothing'
          }
        ],
        equipmentAdjustments: [
          'quarantine.actions.noLiveRock',
          'quarantine.actions.useNeutralFilterPad',
          'quarantine.actions.noVcDechlorinatorFormalin'
        ],
        safetyNotes: []
      },
      {
        day: 'T+17',
        name: 'quarantine.plans.17dayHttm.steps.transferToMainTank',
        type: 'completion',
        actions: ['quarantine.actions.quarantineEnd'],
        safetyNotes: []
      }
    ]
  },
  {
    id: '24day',
    name: 'quarantine.plans.24day.name',
    description: 'quarantine.plans.24day.description',
    duration: 24,
    coverageWarning: 'quarantine.plans.24day.coverageWarning',
    pathogenCoverage: PATHOGEN_COVERAGE,
    fishTypes: ['quarantine.options.fishType.all'],
    warnings: ['quarantine.plans.24day.warnings.praziquantelRequiresUvOff', 'quarantine.plans.24day.warnings.mayCauseWaterCloudiness', 'quarantine.plans.24day.warnings.payAttentionToOxygenation'],
    steps: [
      {
        day: 'T+0~T+3',
        name: 'quarantine.plans.24day.steps.observationPeriod',
        type: 'observation',
        required: true,
        actions: [
          'quarantine.actions.noFeeding',
          'quarantine.actions.noMedication',
          'quarantine.actions.noVcGlucoseYogurt',
          'quarantine.actions.reduceDisturbance'
        ],
        safetyNotes: [
          'quarantine.safetyNotes.transportStress'
        ]
      },
      {
        day: 'T+3',
        name: 'quarantine.plans.24day.steps.firstBathEnterQuarantineTank',
        type: 'bath',
        drugs: [
          {
            name: 'quarantine.medications.formalin',
            dose_ml_per_L: 0.2,
            note: 'Ready‑to‑use 37% formaldehyde solution; do not convert to pure formaldehyde'
          },
          {
            name: 'quarantine.medications.malachiteGreen',
            dose_ml_per_L: 0.1,
            note: '0.1g/100ml stock solution'
          },
          {
            name: 'quarantine.medications.metronidazole',
            dose_mg_per_L: 10,
            note: 'Targets intestinal flagellates'
          },
          {
            name: 'quarantine.medications.hwOdinex',
            dose_ml_per_20L: 1,
            note: 'Maintenance medication for quarantine tank'
          }
        ],
        actions: [
          'Medication bath for 45 minutes',
          'Split dosing: add half first, wait 5 minutes, then add the rest',
          'Oxygenate and ventilate throughout',
          'After bath, transfer to quarantine tank with nitrification system',
          'Add HW Odinex or Cupramine (choose one) + metronidazole 10ppm to quarantine tank'
        ],
        safetyNotes: [
          '⚠ Formalin consumes dissolved oxygen, lack of oxygenation can kill fish',
          '⚠ Formaldehyde gas is toxic, ensure ventilation + wear mask',
          '⚠ If fish struggles violently/floats, immediately change water to rescue'
        ],
        equipmentAdjustments: [
          'Use transparent container for side viewing',
          'Avoid top-view tank (cannot inspect body side)'
        ]
      },
      {
        day: 'T+3~T+10',
        name: 'quarantine.plans.24day.steps.maintenancePeriod',
        type: 'maintenance',
        actions: [
          'quarantine.actions.normalFeeding',
          'quarantine.actions.maintainMedicationConcentration',
          'quarantine.actions.observeFishStatus'
        ],
        safetyNotes: []
      },
      {
        day: 'T+10',
        name: 'quarantine.plans.24day.steps.secondBath',
        type: 'bath',
        drugs: [
          {
            name: 'quarantine.medications.formalin',
            dose_ml_per_L: 0.2,
            note: 'Ready‑to‑use 37% formaldehyde solution; do not convert to pure formaldehyde'
          },
          {
            name: 'quarantine.medications.malachiteGreen',
            dose_ml_per_L: 0.1,
            note: '0.1g/100ml stock solution'
          }
        ],
        actions: [
          'Medication bath for 45 minutes',
          'Split dosing: add half first, wait 5 minutes, then add the rest',
          'Oxygenate and ventilate throughout',
          '100% water change in quarantine tank',
          'Re-add HW Odinex/Cupramine + metronidazole 10ppm',
          'Return fish to tank after bath'
        ],
        safetyNotes: [
          '⚠ Formalin consumes dissolved oxygen, lack of oxygenation can kill fish',
          '⚠ Formaldehyde gas is toxic, ensure ventilation + wear mask',
          '⚠ If fish struggles violently/floats, immediately change water to rescue'
        ],
        equipmentAdjustments: [
          'Use transparent container for side viewing',
          'Avoid top-view tank (cannot inspect body side)'
        ]
      },
      {
        day: 'T+10~T+17',
        name: 'quarantine.plans.24day.steps.maintenancePeriod',
        type: 'maintenance',
        actions: [
          'quarantine.actions.normalFeeding',
          'quarantine.actions.maintainMedicationConcentration',
          'quarantine.actions.observeFishStatus'
        ],
        safetyNotes: []
      },
      {
        day: 'T+17',
        name: 'quarantine.plans.24day.steps.thirdBath',
        type: 'bath',
        drugs: [
          {
            name: 'quarantine.medications.formalin',
            dose_ml_per_L: 0.2,
            note: 'Ready‑to‑use 37% formaldehyde solution; do not convert to pure formaldehyde'
          },
          {
            name: 'quarantine.medications.malachiteGreen',
            dose_ml_per_L: 0.1,
            note: '0.1g/100ml stock solution'
          }
        ],
        actions: [
          'Medication bath for 45 minutes',
          'Split dosing: add half first, wait 5 minutes, then add the rest',
          'Oxygenate and ventilate throughout',
          '100% water change in quarantine tank',
          'Re-add HW Odinex/Cupramine + metronidazole 10ppm',
          'Return fish to tank after bath'
        ],
        safetyNotes: [
          '⚠ Formalin consumes dissolved oxygen, lack of oxygenation can kill fish',
          '⚠ Formaldehyde gas is toxic, ensure ventilation + wear mask',
          '⚠ If fish struggles violently/floats, immediately change water to rescue'
        ],
        equipmentAdjustments: [
          'Use transparent container for side viewing',
          'Avoid top-view tank (cannot inspect body side)'
        ]
      },
      {
        day: 'T+17~T+23',
        name: 'quarantine.plans.24day.steps.maintenancePeriod',
        type: 'maintenance',
        actions: [
          'quarantine.actions.normalFeeding',
          'quarantine.actions.maintainMedicationConcentration',
          'quarantine.actions.observeFishStatus'
        ],
        safetyNotes: []
      },
      {
        day: 'T+23',
        name: 'quarantine.plans.24day.steps.praziquantelTreatment',
        type: 'praziTreatment',
        drugs: [
          {
            name: 'quarantine.medications.praziquantel',
            targetPPM: 2.5,
            note: 'Targets intestinal flukes and tapeworms'
          },
          {
            name: 'PraziPro',
            dose_ml_per_75L: 5,
            note: 'Commercial product; use according to instructions'
          }
        ],
        actions: [
          '100% water change',
          'Add praziquantel',
          'Self‑decompose within 48 hours, no need to maintain'
        ],
        safetyNotes: [
          'Water cloudiness is normal (heterotrophic bacteria outbreak), pay attention to oxygenation'
        ],
        equipmentAdjustments: [
          '❌ Turn off UV, ozone',
          '✅ Run skimmer without collection cup (to prevent overflow)',
          '❌ No activated carbon/adsorbents',
          '❌ Do not combine with copper medication'
        ]
      },
      {
        day: 'T+24',
        name: 'quarantine.plans.24day.steps.completion',
        type: 'completion',
        actions: [
          'quarantine.actions.fullObservation',
          'quarantine.actions.confirmHealth'
        ],
        safetyNotes: []
      }
    ]
  }
]

// 剂量计算函数 - 严谨计算，确保用药安全
export function calculateMedicationDosage(
  medicationKey: string,
  targetPPM: number,
  waterVolume: number,
  customConcentration?: number
): { dosage: number; unit: string; notes: string } {
  const spec = MEDICATION_SPECS[medicationKey as keyof typeof MEDICATION_SPECS]
  
  if (!spec) {
    return { dosage: 0, unit: 'unknown', notes: '⚠️ Unknown medication; please check medication name' }
  }

  let dosage = 0
  let notes = spec.notes || ''
  let safetyWarning = ''

  // 根据药品类型进行严谨计算
  switch (medicationKey) {
    case 'formalin':
      // 福尔马林：37%甲醛溶液，200ppm = 每L水加0.2ml成品福尔马林
      // 计算：targetPPM (mg/L) × waterVolume (L) ÷ 浓度 (mg/ml)
      // 37%福尔马林 = 370mg/ml甲醛
      dosage = (targetPPM * waterVolume) / spec.concentration
      notes += ` | Add ${(targetPPM/spec.concentration).toFixed(4)}ml formalin per L water`
      safetyWarning = '⚠️ Formalin consumes dissolved oxygen; oxygenate throughout!'
      break
    
    case 'praziquantel':
      // 吡喹酮：通常使用母液形式
      const concentration = customConcentration || spec.concentration
      dosage = (targetPPM * waterVolume) / concentration
      notes += ` | Stock solution concentration: ${concentration}mg/ml`
      safetyWarning = '⚠️ Praziquantel requires UV and activated carbon off; may cause water cloudiness'
      break
    
    case 'chloroquine-phosphate':
      // 磷酸氯喹：纯品粉末，直接计算mg
      dosage = targetPPM * waterVolume
      safetyWarning = '⚠️ Disable nitrifying filter media; maintain medication concentration via water changes'
      break
    
    case 'metronidazole':
      // 甲硝唑：纯品粉末，直接计算mg
      dosage = targetPPM * waterVolume
      break
    
    case 'malachite-green':
      // 孔雀石绿：通常使用母液
      const mgConcentration = customConcentration || spec.concentration
      dosage = (targetPPM * waterVolume) / mgConcentration
      notes += ` | Stock solution concentration: ${mgConcentration}mg/ml`
      break
    
    default:
      // 其他药品：PPM = mg有效成分 / L水
      const medConcentration = customConcentration || spec.concentration
      dosage = (targetPPM * waterVolume) / medConcentration
  }

  // 添加安全警告
  if (safetyWarning) {
    notes = safetyWarning + ' | ' + notes
  }

  return {
    dosage: Math.round(dosage * 100) / 100, // 保留2位小数，确保精度
    unit: spec.unit,
    notes
  }
}

// 检疫计划管理
export function useQuarantinePlan() {
  const selectedPlan = ref<QuarantinePlan | null>(null)
  const waterVolume = ref<number>(20) // 默认值设为20L
  const startDate = ref<string>('')
  const currentStep = ref<number>(0)
  const completedSteps = ref<Set<number>>(new Set())

  // 计算当前天数
  const currentDay = computed(() => {
    if (!startDate.value) return 0
    const start = new Date(startDate.value)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - start.getTime())
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    console.log(`Calculating quarantine days: start date=${startDate.value}, current time=${now.toISOString()}, days difference=${days}`)
    return days
  })

  // 获取当前步骤
  const currentSteps = computed(() => {
    if (!selectedPlan.value) return []
    return selectedPlan.value.steps.filter(step => {
      // 解析天数范围，如 'T+0~T+3' 或 'T+3'
      const dayMatch = step.day.match(/T\+(\d+)(?:~T\+(\d+))?/)
      if (!dayMatch) return false
      
      const startDay = parseInt(dayMatch[1])
      const endDay = dayMatch[2] ? parseInt(dayMatch[2]) : startDay
      
      return currentDay.value >= startDay && currentDay.value <= endDay
    })
  })

  // 计算药品剂量
  const calculateStepMedications = (step: QuarantineStep) => {
    if (!step.drugs || waterVolume.value <= 0) return step

    const updatedStep = { ...step }
    // 这里可以根据需要实现剂量计算逻辑
    return updatedStep
  }

  // 标记步骤完成
  const completeStep = (stepIndex: number, fishStatus?: FishStatus) => {
    if (!selectedPlan.value) return

    const step = selectedPlan.value.steps[stepIndex]
    if (step) {
      step.isCompleted = true
      step.completedDate = new Date().toISOString().split('T')[0]
      if (fishStatus) {
        step.fishStatus = fishStatus
      }
      completedSteps.value.add(stepIndex)
      
      // 强制触发响应式更新
      selectedPlan.value = { ...selectedPlan.value }
      
      console.log(`Step ${step.name} marked as completed, index: ${stepIndex}`)
    }
  }

  // 重置计划
  const resetPlan = () => {
    selectedPlan.value = null
    waterVolume.value = 0
    startDate.value = ''
    currentStep.value = 0
    completedSteps.value.clear()
  }

  return {
    selectedPlan,
    waterVolume,
    startDate,
    currentDay,
    currentSteps,
    completedSteps,
    calculateStepMedications,
    completeStep,
    resetPlan
  }
}
